import { useEffect, useState, useCallback } from "react";
import { Navigate, useParams, useNavigate } from "react-router-dom";
import { Loader2, ArrowLeft, Archive, ArchiveRestore } from "lucide-react";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import { apiClient } from "@/lib/api";
import { useAdminAuth } from "@/context/AdminAuthContext";
import AdminShell from "@/components/admin/AdminShell";
import { CONTACT_TYPE_LABELS, CASE_STATUS_LABELS, formatDate, formatDateTime } from "@/constants/business_status";

export default function ContactDetailPage() {
  const { admin } = useAdminAuth();
  const { id } = useParams();
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const r = await apiClient.get(`/admin/contacts/${id}`);
      setData(r.data);
    } catch { toast.error("Contact introuvable"); navigate("/admin/contacts"); }
    finally { setLoading(false); }
  }, [id, navigate]);
  useEffect(() => { if (admin) load(); }, [admin, load]);

  if (admin === null) return <div className="min-h-screen flex items-center justify-center"><Loader2 className="w-6 h-6 animate-spin text-navy" /></div>;
  if (admin === false) return <Navigate to="/admin/login" replace />;
  if (loading || !data) return <AdminShell><div className="py-16 text-center"><Loader2 className="w-6 h-6 animate-spin mx-auto text-navy" /></div></AdminShell>;

  const { contact, organizations, cases } = data;
  const doArchive = async () => { if (!window.confirm("Archiver ce contact ?")) return; await apiClient.post(`/admin/contacts/${id}/archive`); toast.success("Archivé"); load(); };
  const doRestore = async () => { await apiClient.post(`/admin/contacts/${id}/restore`); toast.success("Restauré"); load(); };

  return (
    <AdminShell>
      <div className="mb-5 flex items-center justify-between gap-3 flex-wrap">
        <div>
          <Button data-testid="contact-back" variant="ghost" onClick={() => navigate("/admin/contacts")} className="text-navy font-semibold -ml-3"><ArrowLeft className="w-4 h-4 mr-1" />Retour</Button>
          <h1 data-testid="contact-detail-header" className="font-heading font-extrabold text-2xl text-navy">{contact.display_name}</h1>
          <p className="text-xs text-muted-foreground mt-1">
            {CONTACT_TYPE_LABELS[contact.contact_type]} • Créé le {formatDate(contact.created_at)} • Mis à jour le {formatDate(contact.updated_at)}
            {contact.archived_at ? " • Archivé" : ""}
          </p>
        </div>
        <div className="flex gap-2">
          {contact.archived_at
            ? <Button data-testid="contact-restore" onClick={doRestore} variant="outline" className="rounded-full"><ArchiveRestore className="w-4 h-4 mr-1" /> Restaurer</Button>
            : <Button data-testid="contact-archive" onClick={doArchive} variant="outline" className="rounded-full text-orange border-orange/40 hover:bg-orange/5"><Archive className="w-4 h-4 mr-1" /> Archiver</Button>}
        </div>
      </div>

      <div className="grid md:grid-cols-2 gap-4">
        <div className="border border-gray-100 rounded-2xl bg-white p-5" data-testid="contact-section-coords">
          <h3 className="font-heading font-semibold text-navy text-sm mb-3">Coordonnées</h3>
          <div className="space-y-2 text-sm">
            <p><span className="font-medium text-navy/70 text-xs">Téléphone :</span> {contact.phone || "—"}</p>
            <p><span className="font-medium text-navy/70 text-xs">Email :</span> {contact.email || "—"}</p>
            <p><span className="font-medium text-navy/70 text-xs">Contact préféré :</span> {contact.preferred_contact_method || "—"}</p>
            {contact.notes && <p className="text-muted-foreground pt-2 border-t border-gray-100">{contact.notes}</p>}
          </div>
        </div>

        <div className="border border-gray-100 rounded-2xl bg-white p-5" data-testid="contact-section-orgs">
          <h3 className="font-heading font-semibold text-navy text-sm mb-3">Organisations liées</h3>
          {organizations.length === 0 ? <p className="text-muted-foreground text-sm">Aucune</p> : (
            <ul className="space-y-1 text-sm">
              {organizations.map((o) => (
                <li key={o.id}>
                  <button onClick={() => navigate(`/admin/organisations/${o.id}`)} className="text-navy hover:text-orange font-medium">
                    {o.legal_name} <span className="text-xs text-muted-foreground">({o.organization_type})</span>
                  </button>
                </li>
              ))}
            </ul>
          )}
        </div>

        <div className="border border-gray-100 rounded-2xl bg-white p-5 md:col-span-2" data-testid="contact-section-cases">
          <h3 className="font-heading font-semibold text-navy text-sm mb-3">Dossiers ({cases.length})</h3>
          {cases.length === 0 ? <p className="text-muted-foreground text-sm">Aucun dossier</p> : (
            <div className="divide-y divide-gray-100">
              {cases.map((c) => (
                <button key={c.id} onClick={() => navigate(`/admin/dossiers/${c.id}`)} className="w-full text-left py-2 hover:bg-graylight rounded px-2 flex justify-between items-center">
                  <span>
                    <span className="font-semibold text-navy text-sm">{c.case_number}</span>
                    <span className="text-xs text-muted-foreground ml-2">{c.service_type} • {formatDate(c.created_at)}</span>
                  </span>
                  <span className="text-xs font-semibold bg-navy/5 text-navy rounded-full px-2 py-0.5">{CASE_STATUS_LABELS[c.status] || c.status}</span>
                </button>
              ))}
            </div>
          )}
        </div>
      </div>
    </AdminShell>
  );
}
